import { Heart, Plus, Reply } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useBackState } from "@/book/components/back";
import { ExperienceForm } from "@/book/components/experience-form";
import { MetaLine } from "@/book/components/rows";
import { useExperiences, useToggleLike, type Experience } from "@/shared/api/book";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { Chip } from "@/shared/ui/chip";
import { Markdown } from "@/shared/ui/markdown";
import { SectionLabel } from "@/shared/ui/section-label";
import { Sheet } from "@/shared/ui/sheet";
import { Empty, ErrorNote, ListSkeleton, Pager, StatusChip } from "@/shared/ui/state";

/** Egy tapasztalat kártyája: szerző, szöveg, címkék (fejezet + **kattintható** gyakorlat),
 *  like, válasz és a válaszok. Minden listában ugyanez jelenik meg.
 *
 *  A **saját** tapasztalat nem kedvelhető, és „nekem is van" válasz sem adható rá (a szerver
 *  is elutasítja) — a kapott kedvelések számát viszont a szerzője is látja. */
export function ExperienceCard({
  experience,
  onReply,
  hideExerciseId,
  hideChapters = false,
}: {
  experience: Experience;
  onReply: () => void;
  /** Annak a gyakorlatnak a címkéjét elhagyjuk, amelyiknek az oldalán állunk. */
  hideExerciseId?: number;
  hideChapters?: boolean;
}) {
  const like = useToggleLike();
  const back = useBackState();
  const exercises = experience.exercises.filter((e) => e.id !== hideExerciseId);
  return (
    <article className="flex flex-col gap-2 border-t border-line pt-3">
      <MetaLine author={experience.author} created_at={experience.created_at} />
      <Markdown className="text-[15px]">{experience.body}</Markdown>

      <div className="flex flex-wrap items-center gap-1.5">
        {!hideChapters &&
          experience.chapters.map((c) => <Chip key={c.id}>{c.number}. fejezet</Chip>)}
        {exercises.map((e) => (
          <Link
            key={e.id}
            to={`/app/book/gyakorlatok/${e.id}`}
            state={back}
            className="rounded-chip border border-line px-2 py-1 text-[11px] font-semibold text-gold-text hover:bg-paper"
          >
            {e.title}
          </Link>
        ))}
        <StatusChip status={experience.status} />
      </div>

      {experience.mine ? (
        experience.like_count > 0 && (
          <p
            aria-label={`${experience.like_count} kedvelés`}
            className="flex h-11 items-center gap-1.5 px-2 text-[13px] font-semibold text-muted"
          >
            <Heart size={18} aria-hidden />
            {experience.like_count}
          </p>
        )
      ) : (
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={experience.liked_by_me ? "Like visszavonása" : "Tetszik"}
            aria-pressed={experience.liked_by_me}
            disabled={like.isPending}
            onClick={() => like.mutate({ id: experience.id, liked: experience.liked_by_me })}
            className="flex h-11 items-center gap-1.5 rounded-md px-2 text-[13px] font-semibold text-muted"
          >
            <Heart
              size={18}
              className={cn(experience.liked_by_me && "text-gold")}
              fill={experience.liked_by_me ? "currentColor" : "none"}
            />
            {experience.like_count > 0 && experience.like_count}
          </button>
          <button
            type="button"
            onClick={onReply}
            className="flex h-11 items-center gap-1.5 rounded-md px-2 text-[13px] font-semibold text-muted"
          >
            <Reply size={18} />
            Nekem is van ezzel tapasztalatom
          </button>
        </div>
      )}

      {experience.replies.length > 0 && (
        <div className="flex flex-col gap-3 border-l-2 border-line pl-3.5">
          {experience.replies.map((r) => (
            <div key={r.id} className="flex flex-col gap-1">
              <MetaLine author={r.author} created_at={r.created_at} />
              <Markdown className="text-[14px]">{r.body}</Markdown>
              <StatusChip status={r.status} />
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

/** Egy adott dologhoz (gyakorlat vagy fejezet) tartozó tapasztalatok — **helyben**,
 *  lapozhatóan, saját beküldő- és válasz-panellel.
 *
 *  A lapozás állapota a komponensben él (nem az URL-ben): a gyakorlat- és fejezet-oldalon
 *  ez egy beágyazott lista, nem önálló nézet. */
export function ExperienceList({
  chapterId,
  exerciseId,
  pageSize = 5,
  label = "Tapasztalatok",
  emptyText = "Erre még nincs megosztott tapasztalat.",
  shareLabel = "Megosztom a tapasztalatom",
}: {
  chapterId?: number;
  exerciseId?: number;
  pageSize?: number;
  /** `null`: nincs szekció-címke (a fejezet-oldalon a lapfül már megnevezi a listát). */
  label?: string | null;
  emptyText?: string;
  shareLabel?: string;
}) {
  const [page, setPage] = useState(1);
  const [shareOpen, setShareOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<Experience | null>(null);
  const { data, isPending, isError } = useExperiences({
    chapter_id: chapterId,
    exercise_id: exerciseId,
    page,
    page_size: pageSize,
  });

  return (
    <section className="flex flex-col">
      <div className="mb-2 flex items-center justify-between gap-3">
        {label === null ? (
          <span />
        ) : (
          <SectionLabel>
            {label}
            {data && data.total > 0 && <span className="ml-1.5 text-muted">({data.total})</span>}
          </SectionLabel>
        )}
        <Button size="sm" variant="ghost" onClick={() => setShareOpen(true)}>
          <Plus size={16} />
          Megosztok én is
        </Button>
      </div>

      {isPending && <ListSkeleton rows={2} />}
      {isError && <ErrorNote />}
      {data && data.items.length === 0 && <Empty>{emptyText}</Empty>}

      {data && data.items.length > 0 && (
        <>
          <div className="flex flex-col gap-4">
            {data.items.map((x) => (
              <ExperienceCard
                key={x.id}
                experience={x}
                hideExerciseId={exerciseId}
                hideChapters={chapterId !== undefined}
                onReply={() => setReplyTo(x)}
              />
            ))}
          </div>
          <Pager page={data.page} total={data.total} pageSize={data.page_size} onChange={setPage} />
        </>
      )}

      <Sheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        title={shareLabel}
        description="Jóváhagyás után jelenik meg — addig csak te látod."
      >
        <ExperienceForm
          chapterId={chapterId}
          exerciseId={exerciseId}
          onDone={() => setShareOpen(false)}
        />
      </Sheet>

      <Sheet
        open={replyTo !== null}
        onOpenChange={(v) => !v && setReplyTo(null)}
        title="Nekem is van ezzel tapasztalatom"
        description={replyTo ? `Válasz erre: ${replyTo.author}` : undefined}
      >
        {replyTo && <ExperienceForm parentId={replyTo.id} onDone={() => setReplyTo(null)} />}
      </Sheet>
    </section>
  );
}
